import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CInputFile,
  CInputRadio,
  CLabel,
  CLink,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CSelect,
  CTabContent,
  CTabPane,
  CTabs,
} from "@coreui/react";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getItemPresignedUrl,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import {
  defaultValueOnUndefined,
  emptyValueOnUndefined,
} from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import Datetime from "react-datetime";
import moment from "moment";
import StoreExpense, {
  newStoreExpense,
  STOREEXPENSEMETHODS,
} from "../../models/store-expense";
import CurrencyField from "../currencies/CurrencyField";
import { PYG } from "../../currency/available-currencies";
import SingleStoreSelect from "../store/SingleStoreSelect";
import UserSelect from "../user/UserSelect";
import Store from "../../models/store";
import User from "../../models/user";
import { formatToCurrency } from "../../currency/format";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import SupplierSelect from "../supplier/SupplierSelect";
import Supplier from "../../models/supplier";

import {
  newUniqueListItem,
  UniqueListItem,
  uniqueListToListItems,
} from "../../models/unique-list-item";
import AccountingEntry from "../../models/accounting-entries";
import AccountingEntriesGroup from "../../models/accounting-entries-group";
import AccountingEntryFormItem from "../accounting-entries-groups/AccountingEntryFormItem";
import { BsFillPlusCircleFill } from "react-icons/bs";

interface StoreExpenseFormProps {
  initialStoreExpense?: StoreExpense;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const SupplierForm: React.FC<StoreExpenseFormProps> = ({
  initialStoreExpense,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingItem, setEditingItem] = useState<StoreExpense>(
    initialStoreExpense ? initialStoreExpense : newStoreExpense()
  );
  const [selectedStore, setSelectedStore] = useState<Store | undefined>(
    undefined
  );
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [selectedSupplier, setSelectedSupplier] = useState<
    Supplier | undefined
  >(undefined);

  const [hasLegalInvoice, setHasLegalInvoice] = useState(false);
  const [selectedRevertUser, setSelectedRevertUser] = useState<
    User | undefined
  >(undefined);
  const [orderDate, setOrderDate] = useState<string | moment.Moment>("");
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const [loadedLogoPath, setLoadedLogoPath] = useState<string | undefined>(
    initialStoreExpense?.attachmentUrl
  );

  const logo = useRef<HTMLInputElement>(null);
  const logoFileName = useRef<string>("");

  const logoModified = useRef<boolean>(false);
  const [items, setItems] = useState<UniqueListItem<AccountingEntry>[]>([]);

  const onDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      description: e.target.value,
    });
  };

  const onInvoiceNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      invoiceNumber: e.target.value,
    });
  };

  const onSupplierNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      supplierName: e.target.value,
    });
  };

  const onStampChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      stamp: e.target.value,
    });
  };

  const onSupplierRucChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      supplierRuc: e.target.value,
    });
  };

  const onSupplierChange = (newSupplier: Supplier | null) => {
    setSelectedSupplier(newSupplier !== null ? newSupplier : undefined);
    setEditingItem({
      ...editingItem,
      supplierId: newSupplier !== null ? newSupplier.id : undefined,
      supplierName:
        newSupplier !== null
          ? newSupplier.legalName || newSupplier.name
          : undefined,
      supplierRuc: newSupplier !== null ? newSupplier.ruc : undefined,
    });
  };

  const onHasLegalInvoiceChange = (hasInvoice: boolean) => {
    setHasLegalInvoice(hasInvoice);
    setEditingItem({
      ...editingItem,
      hasLegalInvoice: hasInvoice,
    });
  };

  const onUserChange = (newUser: User | null) => {
    setSelectedUser(newUser !== null ? newUser : undefined);
    setEditingItem({
      ...editingItem,
      userId: newUser !== null ? newUser.id : undefined,
      userName: newUser !== null ? newUser.username : undefined,
    });
  };

  const onRevertUserChange = (newUser: User | null) => {
    setSelectedRevertUser(newUser !== null ? newUser : undefined);
    setEditingItem({
      ...editingItem,
      revertUserId: newUser !== null ? newUser.id : undefined,
      revertUserName: newUser !== null ? newUser.username : undefined,
    });
  };

  const onDatehange = (value: string | moment.Moment) => {
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) {
      return;
    }

    setOrderDate(value);

    if (moment.isMoment(value)) {
      setEditingItem({
        ...editingItem,
        expenseDate: value.toISOString(),
      });
    } else if (value !== undefined && value !== "") {
      setEditingItem({
        ...editingItem,
        expenseDate: value,
      });
    }
  };

  const onStoreChange = (newStore: Store | null) => {
    setSelectedStore(newStore !== null ? newStore : undefined);
    setEditingItem({
      ...editingItem,
      storeId: newStore !== null ? newStore.id : undefined,
      storeName: newStore !== null ? newStore.name : undefined,
    });
  };

  const onRevertDescriptionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditingItem({
      ...editingItem,
      revertDescription: e.target.value,
    });
  };

  const onIVA5Change = (newAmount: number | undefined) => {
    setEditingItem({
      ...editingItem,
      iva5: newAmount,
    });
  };

  const onIVAExemptChange = (newAmount: number | undefined) => {
    setEditingItem({
      ...editingItem,
      ivaExempt: newAmount,
    });
  };

  const onIVA10Change = (newAmount: number | undefined) => {
    setEditingItem({
      ...editingItem,
      iva10: newAmount,
    });
  };

  const onExpenseInvoiceTypeChange = (newExpenseInvoiceType: string) => {
    setEditingItem({
      ...editingItem,
      expenseInvoiceType: newExpenseInvoiceType,
    });
  };

  const onPriceChange = (newAmount: number | undefined) => {
    setEditingItem({
      ...editingItem,
      price: newAmount,
    });
  };

  const onMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingItem({
      ...editingItem,
      paymentMethod: e.target.value,
    });
  };

  const onLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    logoModified.current = true;
    let fileParts = e.target.value.split("\\");
    logoFileName.current = fileParts[fileParts.length - 1];
    setEditingItem({
      ...editingItem,
      attachmentUrl: logoFileName.current,
    });
  };

  const onAddSupplierClick = async () => {
    setSubmitting(true);
    let toSendSupplier: Supplier = {
      companyId: company.id,
      name: editingItem.supplierName,
      legalName: editingItem.supplierName,
      ruc: editingItem.supplierRuc,
    };
    let requestPromise: Promise<ItemRequestStatus<Supplier>>;
    if (toSendSupplier.id === undefined) {
      requestPromise = createItem<Supplier>(
        "/suppliers/create/",
        toSendSupplier
      );
    } else {
      requestPromise = updateItem<Supplier>(
        `/suppliers/${toSendSupplier.id}/`,
        toSendSupplier
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
    } else {
      setSelectedSupplier(requestStatus.data);
      setEditingItem({
        ...editingItem,
        supplierId: requestStatus.data?.id,
        supplierName: requestStatus.data?.legalName,
        supplierRuc: requestStatus.data?.ruc,
      });
    }
    setSubmitting(false);
  };

  const handleLogoUpload = async () => {
    if (!logoModified.current) {
      return true;
    }

    setSubmitting(true);
    const operationDate = moment().toISOString();
    let toSendExpense = {
      ...editingItem,
      companyId: company.id,
    };
    if (toSendExpense.expenseType === undefined) {
      toSendExpense.expenseType = "in_factory";
    }
    if (editingItem.id === undefined) {
      toSendExpense.status = "registered";
      if (toSendExpense.expenseDate === undefined) {
        toSendExpense.expenseDate = operationDate;
      }
    } else {
      if (selectedRevertUser === undefined) {
        setErrors({ revertUserName: ["Seleccione un usuario"] });
        setSubmitting(false);
        return;
      }
      if (
        editingItem.revertDescription === undefined ||
        editingItem.revertDescription === ""
      ) {
        setErrors({ revertDescription: ["Indique un motivo de reversión"] });
        setSubmitting(false);
        return;
      }
      toSendExpense.status = "reverted";
      toSendExpense.revertDate = operationDate;
      toSendExpense.reverted = true;
    }

    let presignedUrlPromise: Promise<ItemRequestStatus<{ url: string }>>;

    presignedUrlPromise = getItemPresignedUrl<StoreExpense>(
      `/store_expenses/put_logo_url/`,
      toSendExpense
    );

    const logoUrlStatus = await presignedUrlPromise;

    if (logoUrlStatus.status !== SUCCESS) {
      if (logoUrlStatus.errors !== undefined) {
        setErrors(logoUrlStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (logoUrlStatus.detail !== undefined) {
        message = logoUrlStatus.detail;
      }
      errorAlert(message);
      return false;
    }

    if (logoUrlStatus.data === undefined) {
      return false;
    }

    const urlData = logoUrlStatus.data as { url: string };

    if (!logo.current || !logo.current.files) {
      return false;
    }
    const logoFile = logo.current.files[0];
    if (!logoFile) {
      return false;
    }

    const uploadStatus = await fetch(urlData.url!, {
      method: "PUT",
      body: logoFile,
      headers: {
        "Content-type": logoFile.type,
      },
    });

    return uploadStatus.status === 200;
  };

  const onSave = async () => {
    setSubmitting(true);
    const hasLogo = editingItem.attachmentUrl !== undefined;
    const imageUploaded = hasLogo ? await handleLogoUpload() : true;
    if (!imageUploaded) {
      setSubmitting(false);
      return;
    }

    const toSendItems: AccountingEntry[] = uniqueListToListItems(items);

    toSendItems.forEach((item) => {
      item.companyId = company.id;
      item.status = "completed";

      item.date = editingItem.expenseDate;
      item.status = "created";
      if (!item.creditAmount) {
        item.creditAmount = 0;
      }
      if (!item.debitAmount) {
        item.debitAmount = 0;
      }
      if (!item.associatedInvoiceNumber) {
        item.associatedInvoiceNumber = editingItem.invoiceNumber;
      }
      if (!item.associatedRuc) {
        item.associatedRuc = editingItem.supplierRuc;
      }
      if (!item.associatedTaxPayer) {
        item.associatedTaxPayer = editingItem.supplierName;
      }
    });

    let accountData: AccountingEntriesGroup = {
      status: "created",
      companyId: company.id,
      date: editingItem.expenseDate,
      items: toSendItems,
    };
    let toSendExpense: StoreExpense = {
      ...editingItem,
      companyId: company.id,
      accountingGroup: accountData,
    };

    const operationDate = moment().toISOString();

    let formErrors: Record<string, string[]> = {};

    if (toSendExpense.expenseType === undefined) {
      toSendExpense.expenseType = "in_factory";
    }

    if (toSendExpense.description === "") {
      toSendExpense.description = undefined;
    }
    if (toSendExpense.revertDescription === "") {
      toSendExpense.revertDescription = undefined;
    }

    let requestPromise: Promise<ItemRequestStatus<StoreExpense>>;
    if (editingItem.id === undefined) {
      toSendExpense.status = "registered";
      if (toSendExpense.expenseDate === undefined) {
        toSendExpense.expenseDate = operationDate;
      }
      const sueldosYJornalesEntry = toSendItems.find(
        (item) => item.account?.name === "SUELDOS Y JORNALES"
      );

      if (toSendExpense.hasLegalInvoice) {
        if (
          !toSendExpense.invoiceNumber &&
          sueldosYJornalesEntry?.account?.name !== "SUELDOS Y JORNALES"
        ) {
          formErrors.invoiceNumber = ["Debe indicar el número de factura"];
        }
        if (
          !toSendExpense.supplierName &&
          sueldosYJornalesEntry?.account?.name !== "SUELDOS Y JORNALES"
        ) {
          formErrors.supplierName = ["Seleccione un proveedor"];
        }
        if (
          !toSendExpense.supplierRuc &&
          sueldosYJornalesEntry?.account?.name !== "SUELDOS Y JORNALES"
        ) {
          formErrors.supplierRuc = ["Seleccione un proveedor"];
        }
        if (
          (toSendExpense.iva10 || 0) +
            (toSendExpense.iva5 || 0) +
            (toSendExpense.ivaExempt || 0) !==
          toSendExpense.price
        ) {
          formErrors.iva10 = ["Los montos de IVA no suman el total"];
          formErrors.iva5 = ["Los montos de IVA no suman el total"];
          formErrors.ivaExempt = ["Los montos de IVA no suman el total"];
        }
        if (!toSendExpense.expenseInvoiceType) {
          formErrors.expenseInvoiceType = ["Seleccione una condición de venta"];
        }

        if (Object.keys(formErrors).length > 0) {
          setErrors(formErrors);
          setSubmitting(false);
          return;
        }
        if (toSendItems.length === 0) {
          errorAlert("El asiento contable debe tener al menos una entrada");
          setSubmitting(false);
          return;
        }
      }
      if (toSendItems.length === 0) {
        toSendExpense.accountingGroup = undefined;
      }
      requestPromise = createItem<StoreExpense>(
        "/store_expenses/create/",
        toSendExpense
      );
    } else {
      if (selectedRevertUser === undefined) {
        formErrors.revertUserName = ["Seleccione un usuario"];
      }
      if (
        editingItem.revertDescription === undefined ||
        editingItem.revertDescription === ""
      ) {
        formErrors.revertDescription = ["Indique un motivo de reversión"];
      }
      if (Object.keys(formErrors).length > 0) {
        setErrors(formErrors);
        setSubmitting(false);
        return;
      }
      toSendExpense.status = "reverted";
      toSendExpense.revertDate = operationDate;
      toSendExpense.reverted = true;
      requestPromise = updateItem<StoreExpense>(
        `/store_expenses/revert/${toSendExpense.id}/`,
        toSendExpense
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingItem(newStoreExpense());
    setSelectedRevertUser(undefined);
    setSelectedStore(undefined);
    setSelectedUser(undefined);
    setOrderDate("");
    setSelectedSupplier(undefined);
    setHasLegalInvoice(false);
    setLoadedLogoPath(undefined);
    setItems([]);
    logoModified.current = false;
  };

  const onAddEntryClick = () => {
    setItems((items) => {
      const initialValue: AccountingEntry = {};
      if (editingItem.invoiceNumber) {
        initialValue.subject = `Factura Nro: ${editingItem.invoiceNumber}`;
      }
      const newItems = [
        ...items,
        newUniqueListItem<AccountingEntry>(initialValue),
      ];
      return newItems;
    });
  };

  const onEntryChange = (entry: UniqueListItem<AccountingEntry>) => {
    setItems((items) => {
      const newItems = [...items];
      const index = newItems.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newItems;
      }

      newItems[index] = entry;

      return newItems;
    });
  };

  const onEntryDelete = (entry: UniqueListItem<AccountingEntry>) => {
    setItems((items) => {
      const newItems = [...items];

      const index = newItems.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newItems;
      }

      newItems.splice(index, 1);

      return newItems;
    });
  };

  useEffect(() => {
    setEditingItem(
      initialStoreExpense ? initialStoreExpense : newStoreExpense()
    );
    if (initialStoreExpense?.expenseDate === undefined) {
      setOrderDate("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStoreExpense]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  const itemErrors = getFieldErrors("items", errors) as Errors[];

  return (
    <>
      <fieldset disabled={submitting}>
        {editingItem.id === undefined ? (
          <>
            <CFormGroup>
              <CRow className={"mt-2"}>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Sucursal:</CLabel>
                </CCol>
                <CCol>
                  <SingleStoreSelect
                    value={selectedStore ? selectedStore : null}
                    onChange={onStoreChange}
                  ></SingleStoreSelect>
                  <FieldErrors
                    errors={getFieldErrors("storeName", errors) as string[]}
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Usuario:</CLabel>
                </CCol>
                <CCol>
                  <UserSelect
                    value={selectedUser ? selectedUser : null}
                    onChange={onUserChange}
                  ></UserSelect>
                  <FieldErrors
                    errors={getFieldErrors("userName", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow className={"mt-2"}>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Fecha:</CLabel>
                </CCol>
                <CCol>
                  <Datetime
                    className="pl-0"
                    onChange={onDatehange}
                    value={orderDate}
                    locale="es/PY"
                    dateFormat="DD/MM/YYYY"
                    timeFormat="HH:mm"
                    closeOnSelect={true}
                  ></Datetime>
                  <FieldErrors
                    errors={getFieldErrors("orderDate", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Concepto:</CLabel>
                </CCol>
                <CCol>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(editingItem.description)}
                    onChange={onDescriptionChange}
                    placeholder="Motivo del gasto"
                  ></CInput>
                  <FieldErrors
                    errors={getFieldErrors("description", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Método de pago:</CLabel>
                </CCol>
                <CCol>
                  <CSelect
                    type="text"
                    value={emptyValueOnUndefined(editingItem.paymentMethod)}
                    onChange={onMethodChange}
                  >
                    <option value={""} disabled>
                      -----
                    </option>
                    {Object.entries(STOREEXPENSEMETHODS).map(
                      ([key, entry], ix) => {
                        return (
                          <option key={ix} value={key}>
                            {entry}
                          </option>
                        );
                      }
                    )}
                  </CSelect>
                  {/* hasLegalInvoice && (
                    <small>
                      {
                        "Si la condición de venta es contado este campo determina si el asiento será caja o banco"
                      }
                    </small>
                  ) */}
                  <FieldErrors
                    errors={getFieldErrors("paymentMethod", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Este pago:</CLabel>
                </CCol>
                <CCol>
                  <CurrencyField
                    currency={PYG}
                    value={editingItem.price}
                    onChange={onPriceChange}
                  ></CurrencyField>
                  <FieldErrors
                    errors={getFieldErrors("price", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Incluir datos contables:</CLabel>
                </CCol>
                <CCol md={2}>
                  <CRow>
                    <AnimatedCheckbox
                      value={hasLegalInvoice}
                      onChange={onHasLegalInvoiceChange}
                    ></AnimatedCheckbox>
                  </CRow>
                </CCol>
              </CRow>
            </CFormGroup>
            {hasLegalInvoice && (
              <>
                <CTabs activeTab="invoice">
                  <CNav variant="tabs">
                    <CNavItem>
                      <CNavLink data-tab="invoice">
                        Datos de Factura/pago
                      </CNavLink>
                    </CNavItem>

                    <CNavItem>
                      <CNavLink data-tab="accounts">
                        Datos de Asiento Contable
                      </CNavLink>
                    </CNavItem>
                  </CNav>
                  <CTabContent>
                    <CTabPane data-tab="invoice">
                      <CFormGroup className={"mt-2"}>
                        <CRow>
                          <CCol md={2}>
                            <CLabel>N° de Factura:</CLabel>
                          </CCol>
                          <CCol>
                            <CInput
                              type="text"
                              value={emptyValueOnUndefined(
                                editingItem.invoiceNumber
                              )}
                              onChange={onInvoiceNumberChange}
                              placeholder="Nro de Factura"
                            ></CInput>
                            <FieldErrors
                              errors={
                                getFieldErrors(
                                  "invoiceNumber",
                                  errors
                                ) as string[]
                              }
                            ></FieldErrors>
                          </CCol>
                        </CRow>
                      </CFormGroup>
                      <CFormGroup>
                        <CRow>
                          <CCol md={2}>
                            <CLabel>Proveedor:</CLabel>
                          </CCol>
                          <CCol md={10}>
                            <SupplierSelect
                              value={
                                selectedSupplier !== undefined
                                  ? selectedSupplier
                                  : null
                              }
                              onChange={onSupplierChange}
                            ></SupplierSelect>
                          </CCol>
                        </CRow>
                      </CFormGroup>
                      <CFormGroup>
                        <CRow>
                          <CCol md={2}>
                            <CLabel>Nombre Proveedor:</CLabel>
                          </CCol>
                          <CCol md={4}>
                            <CInput
                              type="text"
                              value={emptyValueOnUndefined(
                                editingItem.supplierName
                              )}
                              onChange={onSupplierNameChange}
                              placeholder="Nombre del proveedor"
                              disabled={selectedSupplier !== undefined}
                            ></CInput>
                            <FieldErrors
                              errors={
                                getFieldErrors(
                                  "supplierName",
                                  errors
                                ) as string[]
                              }
                            ></FieldErrors>
                          </CCol>
                          <CCol md={2}>
                            <CLabel>RUC Proveedor:</CLabel>
                          </CCol>
                          <CCol md={4}>
                            <CInput
                              type="text"
                              value={emptyValueOnUndefined(
                                editingItem.supplierRuc
                              )}
                              onChange={onSupplierRucChange}
                              placeholder="RUC proveedor"
                              disabled={selectedSupplier !== undefined}
                            ></CInput>
                            <FieldErrors
                              errors={
                                getFieldErrors(
                                  "supplierRuc",
                                  errors
                                ) as string[]
                              }
                            ></FieldErrors>
                          </CCol>
                        </CRow>
                      </CFormGroup>
                      <CFormGroup>
                        <CRow>
                          <CCol md={2}>
                            <CLabel>Timbrado:</CLabel>
                          </CCol>
                          <CCol>
                            <CInput
                              type="text"
                              value={emptyValueOnUndefined(editingItem.stamp)}
                              onChange={onStampChange}
                              placeholder="Timbrado"
                            ></CInput>
                            <FieldErrors
                              errors={
                                getFieldErrors("stamp", errors) as string[]
                              }
                            ></FieldErrors>
                          </CCol>
                        </CRow>
                      </CFormGroup>
                      <CFormGroup>
                        <CRow>
                          <CCol md={2}>
                            <CLabel>Condición de venta:</CLabel>
                          </CCol>
                          <CCol md={2}>
                            <div className="form-check form-check-inline">
                              <CInputRadio
                                id="saleTypeCash"
                                name="saleType"
                                className="form-check-input"
                                value={"cash"}
                                defaultChecked={
                                  editingItem.expenseInvoiceType !==
                                    undefined &&
                                  editingItem.expenseInvoiceType === "cash"
                                }
                                checked={
                                  editingItem.expenseInvoiceType === "cash"
                                }
                                onChange={() =>
                                  onExpenseInvoiceTypeChange("cash")
                                }
                              ></CInputRadio>
                              <CLabel
                                htmlFor="saleTypeCash"
                                className="form-check-label"
                              >
                                {"Contado"}
                              </CLabel>
                            </div>
                            <FieldErrors
                              errors={
                                getFieldErrors(
                                  "expenseInvoiceType",
                                  errors
                                ) as string[]
                              }
                            ></FieldErrors>
                          </CCol>
                          <CCol md={2}>
                            <div className="form-check form-check-inline">
                              <CInputRadio
                                id="saleTypeCredit"
                                name="saleType"
                                className="form-check-input"
                                value={"credit"}
                                defaultChecked={
                                  editingItem.expenseInvoiceType !==
                                    undefined &&
                                  editingItem.expenseInvoiceType === "credit"
                                }
                                checked={
                                  editingItem.expenseInvoiceType === "credit"
                                }
                                onChange={() =>
                                  onExpenseInvoiceTypeChange("credit")
                                }
                              ></CInputRadio>
                              <CLabel
                                htmlFor="saleTypeCredit"
                                className="form-check-label"
                              >
                                {"Crédito"}
                              </CLabel>
                            </div>
                          </CCol>
                        </CRow>
                      </CFormGroup>
                      <CFormGroup>
                        <CRow>
                          <CCol md={2}>
                            <CLabel>Gravadas 10%:</CLabel>
                          </CCol>
                          <CCol>
                            <CurrencyField
                              currency={PYG}
                              value={editingItem.iva10}
                              onChange={onIVA10Change}
                            ></CurrencyField>
                            <FieldErrors
                              errors={
                                getFieldErrors("iva10", errors) as string[]
                              }
                            ></FieldErrors>
                          </CCol>
                          <CCol md={2}>
                            <CLabel>IVA 10%:</CLabel>
                          </CCol>
                          <CCol>
                            <CurrencyField
                              currency={PYG}
                              value={(editingItem.iva10 || 0) / 11}
                              disabled
                            ></CurrencyField>
                          </CCol>
                        </CRow>
                      </CFormGroup>
                      <CFormGroup>
                        <CRow>
                          <CCol md={2}>
                            <CLabel>Gravadas 5%:</CLabel>
                          </CCol>
                          <CCol>
                            <CurrencyField
                              currency={PYG}
                              value={editingItem.iva5}
                              onChange={onIVA5Change}
                            ></CurrencyField>
                            <FieldErrors
                              errors={
                                getFieldErrors("iva5", errors) as string[]
                              }
                            ></FieldErrors>
                          </CCol>
                          <CCol md={2}>
                            <CLabel>IVA 5%:</CLabel>
                          </CCol>
                          <CCol>
                            <CurrencyField
                              currency={PYG}
                              value={(editingItem.iva5 || 0) / 21}
                              disabled
                            ></CurrencyField>
                          </CCol>
                        </CRow>
                      </CFormGroup>
                      <CFormGroup>
                        <CRow>
                          <CCol md={2}>
                            <CLabel>Exentas:</CLabel>
                          </CCol>
                          <CCol md={4}>
                            <CurrencyField
                              currency={PYG}
                              value={editingItem.ivaExempt}
                              onChange={onIVAExemptChange}
                            ></CurrencyField>
                            <FieldErrors
                              errors={
                                getFieldErrors("ivaExempt", errors) as string[]
                              }
                            ></FieldErrors>
                          </CCol>
                        </CRow>
                      </CFormGroup>
                      <CFormGroup>
                        <CRow>
                          <CCol md={2}>
                            <CLabel>Imagen:</CLabel>
                          </CCol>
                          <CCol>
                            <CInputFile
                              accept=".png, .jpg, .pdf"
                              type="file"
                              innerRef={logo}
                              onChange={onLogoChange}
                            ></CInputFile>
                            {loadedLogoPath ? (
                              <small>
                                Seleccionado actualmente:
                                <CLink href={`#`} target="_blank">
                                  {loadedLogoPath}
                                </CLink>
                              </small>
                            ) : (
                              <></>
                            )}

                            <FieldErrors
                              errors={
                                getFieldErrors("imageUrl", errors) as string[]
                              }
                            ></FieldErrors>
                          </CCol>
                        </CRow>
                      </CFormGroup>
                    </CTabPane>
                    <CTabPane data-tab="accounts">
                      {editingItem.supplierName &&
                        editingItem.supplierRuc &&
                        !editingItem.supplierId && (
                          <CFormGroup>
                            <CRow>
                              <CCol md={3}>
                                <CLabel>
                                  Proveedor escrito de forma manual. Crear?:
                                </CLabel>
                              </CCol>
                              <CCol md={2}>
                                <CButton
                                  color="primary"
                                  className="float-right"
                                  onClick={onAddSupplierClick}
                                >
                                  <BsFillPlusCircleFill />
                                  &nbsp; Crear
                                </CButton>
                              </CCol>
                            </CRow>
                          </CFormGroup>
                        )}
                      {items.map((entry, ix) => {
                        return (
                          <CRow key={entry.uuid}>
                            <CCol>
                              <AccountingEntryFormItem
                                value={entry.item}
                                onDelete={() => onEntryDelete(entry)}
                                onChange={(item) =>
                                  onEntryChange({
                                    uuid: entry.uuid,
                                    item: item,
                                  })
                                }
                                errors={
                                  ix < itemErrors.length ? itemErrors[ix] : {}
                                }
                                initialEntry={entry.item}
                                initialAccount={entry.item?.account}
                              />
                            </CCol>
                          </CRow>
                        );
                      })}
                      {items.length === 0 ? (
                        <CRow>
                          <CCol>
                            Agregue nuevas entradas al asiento con el boton + de
                            la derecha
                          </CCol>
                        </CRow>
                      ) : (
                        <></>
                      )}
                      <CRow className="mb-1">
                        <CCol>
                          <CButton
                            className="btn btn-primary float-right"
                            onClick={onAddEntryClick}
                          >
                            <i className="fa fa-plus" />
                          </CButton>
                        </CCol>
                      </CRow>
                    </CTabPane>
                  </CTabContent>
                </CTabs>
              </>
            )}
          </>
        ) : (
          <>
            <CFormGroup>
              <CRow className={"mt-2"}>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Sucursal:</CLabel>
                </CCol>
                <CCol>
                  <p className="mt-2">{editingItem.storeName}</p>
                </CCol>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Usuario:</CLabel>
                </CCol>
                <CCol>
                  <p className="mt-2">{editingItem.userName}</p>
                </CCol>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Concepto:</CLabel>
                </CCol>
                <CCol>
                  <p className="mt-2">{editingItem.description}</p>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Este pago:</CLabel>
                </CCol>
                <CCol>
                  <p className="mt-2">
                    {formatToCurrency(editingItem.price || 0, PYG)}
                  </p>
                </CCol>
              </CRow>
            </CFormGroup>
            {editingItem.invoiceNumber && (
              <>
                <CFormGroup>
                  <CRow>
                    <CCol md={2}>
                      <CLabel>Proveedor:</CLabel>
                    </CCol>
                    <CCol>
                      <p className="mt-2">
                        {defaultValueOnUndefined("-", editingItem.supplierName)}{" "}
                        {defaultValueOnUndefined("-", editingItem.supplierRuc)}
                      </p>
                    </CCol>
                    <CCol md={2}>
                      <CLabel>N° Factura:</CLabel>
                    </CCol>
                    <CCol>
                      <p className="mt-2">
                        {defaultValueOnUndefined(
                          "-",
                          editingItem.invoiceNumber
                        )}
                      </p>
                    </CCol>
                  </CRow>
                </CFormGroup>
              </>
            )}
            <CFormGroup>
              <CRow className={"mt-2"}>
                <CCol md={2}>
                  <CLabel className={"mt-2"}>Usuario reversión:</CLabel>
                </CCol>
                <CCol>
                  <UserSelect
                    value={selectedRevertUser ? selectedRevertUser : null}
                    onChange={onRevertUserChange}
                  ></UserSelect>
                  <FieldErrors
                    errors={
                      getFieldErrors("revertUserName", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>

            <CFormGroup>
              <CRow>
                <CCol md={2}>
                  <CLabel>Motivo reversión:</CLabel>
                </CCol>
                <CCol>
                  <CInput
                    type="text"
                    value={emptyValueOnUndefined(editingItem.revertDescription)}
                    onChange={onRevertDescriptionChange}
                    placeholder="Motivo de reversión"
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors("revertDescription", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
          </>
        )}

        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton
              type="submit"
              color="primary"
              onClick={onSave}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default SupplierForm;
